// Compact canonical roster for AI ingredient binding.
//
// Sent to generate-recipe (and any other AI-binding edge function)
// so Claude can map its emitted ingredient text back to a known
// canonical_id at GENERATION time, rather than relying on the
// post-hoc alias map. In dream mode (empty pantry, free creative
// authoring) this is the only anchor between the AI's vocabulary
// and our id space.
//
// PHILOSOPHY (per memory feedback_protect_dream_mode):
//   • Don't restrict creativity. Claude is still free to invent any
//     dish, fusion, or technique. The roster only constrains the
//     IDENTITY-BINDING step ("what canonical does this ingredient
//     refer to?").
//   • Allow synthetic proposals. When Claude needs an ingredient
//     that isn't in the roster (Gjetost, Brunost, niche items),
//     it emits `proposedCanonical: { name, hub, category }` instead
//     of a known id, and the edge function lands it in
//     pending_ingredient_info for review.
//
// PAYLOAD SHAPE (one line per canonical):
//   { id, name, hub, category, hint? }
//
// `hint` is included only for canonicals where the bare name is
// ambiguous across English dialects or culinary traditions —
// disambiguates Claude's binding decision via short context phrase.
//
// SIZE BUDGET: ~8K tokens for 640 canonicals at ~12 tokens each.
// Cached via Anthropic prompt caching on the edge function side
// so the per-call cost amortizes.

import { INGREDIENTS, HUBS, dbCanonicalsSnapshot } from "../data/ingredients";

// Disambiguator hints for canonicals whose bare name is dialect-
// or context-ambiguous. Add when admin review surfaces an AI
// binding that picked the wrong canonical.
//
// Format: { canonical_id: "short context phrase ≤ 8 words" }
const HINTS = {
  cookie: "sweet baked treat (US cookie / UK biscuit)",
  // Add: biscuit, chips, jelly, pudding once those canonicals exist.
  pancake: "thick US-style flapjack, not crepe",
  // Cured meats — distinct techniques that Claude conflates.
  bacon: "smoked cured pork strips, US-style streaky",
  guanciale: "italian cured pork jowl, distinct from pancetta/bacon",
  prosciutto: "italian dry-cured ham",
  // Cheeses — common confusions.
  queso_fresco: "fresh latin american cheese, distinct from queso blanco",
  mozzarella: "fresh italian cheese, water-packed or low-moisture",
  feta: "greek brined sheep / sheep+goat cheese",
  // Other commonly conflated.
  buttermilk: "cultured fermented milk, distinct from milk or sour cream",
  heavy_cream: "minimum 36% milkfat, US heavy whipping cream",
  whole_milk: "regular dairy milk (not 2%, skim, or non-dairy)",
};

// Build a compact, AI-binding-friendly roster from the bundled
// registry. Hubs are EXCLUDED (they're grouping constructs, not
// ingredients you bind to).
export function buildCanonicalRoster() {
  const out = [];
  const hubsById = new Map(HUBS.map(h => [h.id, h]));
  for (const ing of INGREDIENTS) {
    if (!ing?.id || String(ing.id).endsWith("_hub")) continue;
    const entry = {
      id: ing.id,
      name: ing.name || ing.id,
      category: ing.category || null,
    };
    if (ing.parentId && hubsById.has(ing.parentId)) {
      entry.hub = ing.parentId;
    }
    if (HINTS[ing.id]) {
      entry.hint = HINTS[ing.id];
    }
    out.push(entry);
  }
  // Synthetic canonicals (user-minted / admin-promoted, from
  // ingredient_info via dbCanonicals) — without these the AI can't
  // bind to slugs the user's own kitchen already knows (gjetost,
  // rookworst), so it re-proposes them as new canonicals every
  // draft. Appended AFTER the bundled block, sorted by id, so the
  // bundled prefix stays byte-identical for prompt caching; the
  // synthetic tail is stable within a session once the DB layer
  // hydrates. Capped to keep the token budget bounded.
  const seen = new Set(out.map((r) => r.id));
  const synthetics = dbCanonicalsSnapshot()
    .filter((ing) => ing?.id && !seen.has(ing.id) && !String(ing.id).endsWith("_hub"))
    .sort((a, b) => String(a.id).localeCompare(String(b.id)))
    .slice(0, 200);
  for (const ing of synthetics) {
    out.push({
      id: ing.id,
      name: ing.name || ing.id,
      category: ing.category || null,
      ...(ing.parentId ? { hub: ing.parentId } : {}),
    });
  }
  return out;
}

// Serialize the roster to a compact text block for inclusion in a
// Claude system prompt. Newline-delimited rows with a stable column
// order keeps the prompt cache stable across calls. Format optimized
// for token efficiency: one line per canonical, omit nullables.
//
// Example output:
//   chicken_breast | Chicken Breast | meat | hub:chicken_hub
//   cookie | Cookie | pantry | hub:bread_hub | hint:sweet baked treat (US cookie / UK biscuit)
export function serializeCanonicalRoster(roster) {
  if (!Array.isArray(roster)) return "";
  return roster
    .map(r => {
      const cells = [r.id, r.name, r.category];
      if (r.hub)  cells.push(`hub:${r.hub}`);
      if (r.hint) cells.push(`hint:${r.hint}`);
      return cells.filter(Boolean).join(" | ");
    })
    .join("\n");
}
